import { useMemo } from 'react';
import { filter, map, Subject } from 'rxjs';
import { match } from 'ts-pattern';
import { Toaster, useErrorToast, useInfoToast, useSuccessToast } from '../hooks/toasters';
import { useSubscription } from '../hooks/useSubscription';
import { BusEvent } from './EditorContext';
import { DocumentState } from './useDocumentState';

const makeNotifications = (recipientName: string | undefined | null) =>
  ({
    LengthError: ['error', 'Error', 'Sorry. Your letter is too long.'],
    FetchError: [
      'error',
      'Something went wrong.',
      "We're having a hard time fetching that letter. Please try again in a moment.",
    ],
    LetterSent: ['success', 'Letter sent 📮', undefined],
    DraftDeleted: ['success', 'Draft deleted 🗑', undefined],
    InvitationalLetterPrepared: [
      'success',
      'Invitational letter prepared',
      'The link to share it can be found here in Invitations.',
    ],
    RateLimit: [
      'info',
      `Please give ${
        recipientName || 'them'
      } a little more time before sending them another letter.`,
      undefined,
    ],
    SomethingWentWrong: ['error', 'Something went wrong. Try again soon.', undefined],
    ConnectivityIssue: [
      'error',
      'Error saving draft.',
      "You may be offline or LetterDesk could be down temporarily. LetterDesk will start saving your draft into your browser's local storage until it can be synced.",
    ],
    ReSynced: ['success', 'Letter successfully re-synced with server', undefined],
  } as const);

type Notifications = ReturnType<typeof makeNotifications>;

export type EditorNotificationEvent = keyof Notifications;

// FX
const mkNotifier =
  (notifications: Notifications, success: Toaster, info: Toaster, error: Toaster) =>
  (e: EditorNotificationEvent) => {
    const [kind, title, description] = notifications[e];
    const notification = { title, description };

    match(kind)
      .with('success', () => success({ ...notification, position: 'top-left' }))
      .with('info', () => info(notification))
      .with('error', () => error(notification))
      .exhaustive();
  };

export const useEditorNotifications = (bus$: Subject<BusEvent>, document: DocumentState) => {
  const notification$ = useMemo(
    () =>
      bus$.pipe(
        filter((e) => e.type === 'Notify'),
        map((e) => (e as { type: 'Notify'; event: EditorNotificationEvent }).event)
      ),
    [bus$]
  );

  const notifications = makeNotifications(document?.first_name);

  const errorToast = useErrorToast();
  const successToast = useSuccessToast();
  const infoToast = useInfoToast();

  const notifier = mkNotifier(notifications, successToast, infoToast, errorToast);

  useSubscription(() => notification$.subscribe(notifier), [notification$, notifier]);
};
